import React, { useState, useCallback } from 'react';
import { array, bool, func, object, arrayOf } from 'prop-types';
import { connect } from 'react-redux';
import { compose } from 'redux';
import { useLocation, useHistory } from 'react-router-dom';
import omit from 'lodash/omit';
import classNames from 'classnames';

import { useIntl, FormattedMessage } from '../../util/reactIntl';
import { useConfiguration } from '../../context/configurationContext';
import { useRouteConfiguration } from '../../context/routeConfigurationContext';
import { parse, stringify } from '../../util/urlHelpers';
import { createResourceLocatorString } from '../../util/routes';
import { propTypes } from '../../util/types';
import { getListingsById } from '../../ducks/marketplaceData.duck';
import { manageDisableScrolling, isScrollingDisabled } from '../../ducks/ui.duck';
import { getListingCategories } from '../../ducks/hostedAssets.duck';
import { getCategoryUrl, getCategoryFilterParams, getCategoryBreadcrumbPath } from '../../util/categoryLandingHelpers';
import { isFieldForCategory } from '../../util/fieldHelpers';
import {
  groupListingFieldConfigs,
  initialValues,
  validFilterParams,
  validUrlQueryParamsFromProps,
  cleanSearchFromConflictingParams,
  omitLimitedListingFieldParams,
} from '../SearchPage/SearchPage.shared';

import { BottomNav, H1, NamedLink, NamedRedirect, Notice, Page } from '../../components';
import TopbarContainer from '../TopbarContainer/TopbarContainer';
import FooterContainer from '../FooterContainer/FooterContainer';
import NotFoundPage from '../NotFoundPage/NotFoundPage';
import SearchResultsPanel from '../SearchPage/SearchResultsPanel/SearchResultsPanel';

import CategoryBreadcrumbs from '../../components/CategoryBreadcrumbs/CategoryBreadcrumbs';
import ShopByCategoryColumn from './ShopByCategoryColumn/ShopByCategoryColumn';
import FilterBar from '../SearchPage/FilterBar/FilterBar';
import FilterPanel from '../SearchPage/FilterPanel/FilterPanel';
import FloatingFilterBar from '../SearchPage/FloatingFilterBar/FloatingFilterBar';
import {
  splitHeadAndOverflowFilters,
  narrowFilterOptions,
  isFilterActive,
} from '../SearchPage/FilterBar/filterBarHelpers';
import CategoryContentSection from '../../components/CategoryContentSection/CategoryContentSection';

import searchCss from '../SearchPage/SearchPage.module.css';
import css from './CategoryPage.module.css';

/**
 * Filter values not yet in the URL, kept for the URL they were picked on: once the URL
 * changes (a pushed filter, an X chip, back/forward) the filters start again from it.
 */
export const useDraftQueryParams = search => {
  const [draft, setDraft] = useState({ search, params: {} });
  const draftQueryParams = draft.search === search ? draft.params : {};
  const setDraftQueryParams = params => setDraft({ search, params });
  return [draftQueryParams, setDraftQueryParams];
};

export const CategoryPageComponent = props => {
  const {
    category,
    breadcrumbPath,
    listings,
    pagination,
    scrollingDisabled,
    searchInProgress,
    searchListingsError,
    listingCategories,
    treeCounts,
    facets,
    config,
    routeConfiguration,
    onManageDisableScrolling,
    params,
    staticContext,
  } = props;

  const location = useLocation();
  const history = useHistory();
  const intl = useIntl();

  const [currentQueryParams, setCurrentQueryParams] = useDraftQueryParams(location.search);

  const [isFilterPanelOpen, setIsFilterPanelOpen] = useState(false);
  const [panelFocusKey, setPanelFocusKey] = useState(null);

  // Config — always available, safe before early return
  const { listingFields: listingFieldsConfig } = config?.listing || {};
  const { defaultFilters: defaultFiltersConfig, sortConfig } = config?.search || {};
  const activeListingTypes = config?.listing?.listingTypes.map(c => c.listingType);
  const marketplaceCurrency = config.currency;

  const urlQueryParams = validUrlQueryParamsFromProps({ location, config });

  // Only show listing fields relevant to this category's breadcrumb path
  const breadcrumbCategoryIds = breadcrumbPath.map(c => c.id);
  const categoryFilteredListingFields = listingFieldsConfig.filter(fieldConfig =>
    isFieldForCategory(breadcrumbCategoryIds, fieldConfig)
  );

  const [customPrimaryFilters, customSecondaryFilters] = groupListingFieldConfigs(
    categoryFilteredListingFields,
    activeListingTypes
  );

  const filteredDefaultFilters = defaultFiltersConfig.filter(f => f.schemaType !== 'category');
  const priceFilters = filteredDefaultFilters.filter(f => f.key === 'price');
  const otherDefaultFilters = filteredDefaultFilters.filter(
    f => !['price', 'keywords', 'dates'].includes(f.key)
  );

  // Hybrid filter row + panel (ledger §2). The category tree is the column's
  // job; every other filter splits into head pills and panel-only overflow.
  const customFilters = [...customPrimaryFilters, ...customSecondaryFilters];
  const panelDefaultFilters = [...priceFilters, ...otherDefaultFilters];
  const { head: headFilters, overflow: overflowFilters } = splitHeadAndOverflowFilters(
    customFilters,
    panelDefaultFilters
  );
  const presence = facets?.presence || null;
  const narrowedHeadFilters = headFilters.map(f =>
    narrowFilterOptions(f, presence, urlQueryParams)
  );
  const panelFilters = [...customFilters, ...panelDefaultFilters];

  const activeFilterCount = [...headFilters, ...overflowFilters].filter(f =>
    isFilterActive(f, urlQueryParams)
  ).length;

  const handleToggleSoldOut = checked => {
    const withoutSoldOut = omit(urlQueryParams, 'showSoldOut');
    pushToCurrentPage(checked ? { ...withoutSoldOut, showSoldOut: 'true' } : withoutSoldOut);
  };

  const selectedFilters = validFilterParams(urlQueryParams, listingFieldsConfig, defaultFiltersConfig);
  const selectedFiltersCountForMobile = Object.keys(selectedFilters).length;

  // Navigate to current page URL with new filter params
  const pushToCurrentPage = useCallback(
    queryParams => {
      const search = stringify(queryParams);
      history.push(`${location.pathname}${search ? `?${search}` : ''}`);
    },
    [history, location.pathname]
  );

  const resetAll = useCallback(() => {
    // Clear every filter but STAY on this category's landing page — this
    // used to bounce to the catalog route (legacy of mirroring SearchPage)
    setCurrentQueryParams({});
    history.push(location.pathname);
  }, [history, location.pathname]);

  const getHandleChangedValueFn = useCallback(
    useHistoryPush => updatedURLParams => {
      // If a category filter changed, navigate to that category's landing page
      const categoryLevelKeys = ['pub_categoryLevel1', 'pub_categoryLevel2', 'pub_categoryLevel3', 'pub_categoryLevel4'];
      const hasCategoryChange = categoryLevelKeys.some(k => k in updatedURLParams);
      if (hasCategoryChange && useHistoryPush) {
        // Use the deepest level that has a value set
        const changedCategoryKey = [...categoryLevelKeys].reverse().find(k => updatedURLParams[k]);
        const categoryId = changedCategoryKey ? updatedURLParams[changedCategoryKey] : null;
        const categoryPath = categoryId ? getCategoryUrl(categoryId) : null;

        // Strip category-level keys from current URL params
        const nonCategoryParams = Object.keys(urlQueryParams).reduce((acc, k) => {
          if (!categoryLevelKeys.includes(k)) acc[k] = urlQueryParams[k];
          return acc;
        }, {});

        const filterConfigs = { listingFieldsConfig, defaultFiltersConfig, listingCategories };

        if (categoryPath) {
          // Navigating to a different category — build target category level params so
          // omitLimitedListingFieldParams knows which filters are still compatible
          const targetBreadcrumb = getCategoryBreadcrumbPath(categoryId);
          const targetCategoryLevelParams = targetBreadcrumb.reduce((acc, cat, index) => {
            acc[`pub_categoryLevel${index + 1}`] = cat.id;
            return acc;
          }, {});
          // Strip filters not applicable to the target category, then drop the level keys (they're in the URL path)
          const filtered = omitLimitedListingFieldParams(
            { ...nonCategoryParams, ...targetCategoryLevelParams },
            filterConfigs
          );
          categoryLevelKeys.forEach(k => delete filtered[k]);
          const search = stringify(filtered);
          history.push(`${categoryPath}${search ? `?${search}` : ''}`);
        } else {
          // Category cleared — go to /s, strip ALL category-limited filters since no category is active
          const filtered = omitLimitedListingFieldParams(nonCategoryParams, filterConfigs);
          history.push(createResourceLocatorString('SearchPage', routeConfiguration, {}, filtered));
        }
        return;
      }

      const merged = useHistoryPush
        ? { ...urlQueryParams, ...updatedURLParams }
        : { ...urlQueryParams, ...currentQueryParams, ...updatedURLParams };
      const cleaned = cleanSearchFromConflictingParams(
        merged,
        listingFieldsConfig,
        defaultFiltersConfig,
        sortConfig
      );
      setCurrentQueryParams(cleaned);
      if (useHistoryPush) {
        pushToCurrentPage(cleaned);
      }
    },
    [urlQueryParams, currentQueryParams, listingFieldsConfig, defaultFiltersConfig, listingCategories, sortConfig, pushToCurrentPage, history, routeConfiguration]
  );

  const handleSortBy = useCallback(
    (urlParam, values) => {
      const queryParams = values
        ? { ...urlQueryParams, [urlParam]: values }
        : omit(urlQueryParams, urlParam);
      pushToCurrentPage(queryParams);
    },
    [urlQueryParams, pushToCurrentPage]
  );

  // Early return after all hooks
  if (!category) {
    return <NotFoundPage staticContext={staticContext} />;
  }

  // The hierarchical form /{ancestor}/{category} lives at the category's own URL
  if (params?.slug2) {
    return (
      <NamedRedirect
        name="CategoryPage1Seg"
        params={{ slug: category.url_slug }}
        search={location.search}
      />
    );
  }

  // Pre-populate category filter with current category so it shows as selected
  const categoryParams = getCategoryFilterParams(category, breadcrumbPath);
  const locationWithCategory = {
    ...location,
    search: stringify({ ...categoryParams, ...parse(location.search) }),
  };
  const filterInitialValues = initialValues({ location: locationWithCategory, config }, currentQueryParams);

  // Mirrors the duck's facets query (category scope + active filters) so the
  // active-field exception can re-issue it minus one field's own param
  const facetsParams = { ...categoryParams, ...urlQueryParams };

  const hasPaginationInfo = !!pagination && pagination.totalItems != null;
  const totalItems = hasPaginationInfo ? pagination.totalItems : 0;
  const listingsAreLoaded = !searchInProgress && hasPaginationInfo;

  // Page config
  const pageConfig = category.pageConfig || {};
  const pageHeading = pageConfig.pageHeading || category.name;
  const description = pageConfig.description;
  const heroImage = pageConfig.heroImage;
  const videoUrl = pageConfig.videoUrl;
  const mediaAttribution = pageConfig.mediaAttribution;
  const mediaAttributionName = pageConfig.mediaAttributionName;
  const mediaAttributionUrl = pageConfig.mediaAttributionUrl;
  const hasContent = pageConfig.locked && description && (heroImage || videoUrl);

  // Extract YouTube video ID for schema and thumbnail derivation
  const getYouTubeId = url => {
    if (!url) return null;
    const regExp = /^.*(youtu.be\/|v\/|u\/\w\/|embed\/|watch\?v=|&v=)([^#&?]*).*/;
    const match = url.match(regExp);
    return match && match[2].length === 11 ? match[2] : null;
  };
  const youtubeId = getYouTubeId(videoUrl);
  const youtubeThumbnail = youtubeId
    ? `https://img.youtube.com/vi/${youtubeId}/maxresdefault.jpg`
    : null;
  const videoEmbedUrl = youtubeId ? `https://www.youtube.com/embed/${youtubeId}` : null;

  const siteTitle = config.marketplaceName;
  const metaTitle = pageConfig.metaTitle || `${category.name} | ${siteTitle}`;
  const metaDescription =
    pageConfig.metaDescription ||
    intl.formatMessage(
      { id: 'CategoryPage.metaDescription' },
      { categoryName: category.name, marketplaceName: siteTitle }
    );

  const breadcrumbSchema = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      {
        '@type': 'ListItem',
        position: 1,
        name: intl.formatMessage({ id: 'CategoryPage.breadcrumbHome' }),
        item: `${config.marketplaceRootURL}`,
      },
      ...breadcrumbPath.map((cat, index) => ({
        '@type': 'ListItem',
        position: index + 2,
        name: cat.name,
        item: `${config.marketplaceRootURL}${getCategoryUrl(cat.id)}`,
      })),
    ],
  };

  const schemaImage = heroImage || youtubeThumbnail || null;
  const collectionSchema = {
    '@context': 'https://schema.org',
    '@type': 'CollectionPage',
    name: pageHeading,
    description: metaDescription,
    url: `${config.marketplaceRootURL}${location.pathname}`,
    ...(schemaImage ? { image: schemaImage } : {}),
    ...(pageConfig.lastUpdated ? { dateModified: pageConfig.lastUpdated } : {}),
    ...(pageConfig.keyFacts?.length > 0 ? { keywords: pageConfig.keyFacts.join(', ') } : {}),
  };

  const openGraphImages = heroImage
    ? [{ url: heroImage, width: 1200, height: 630 }]
    : youtubeThumbnail
    ? [{ url: youtubeThumbnail, width: 1280, height: 720 }]
    : config.branding?.socialSharingImage
    ? [{ url: config.branding.socialSharingImage.url }]
    : [];

  const videoUploadDate = pageConfig.videoUploadDate;
  const videoSchema =
    videoEmbedUrl && videoUploadDate
      ? {
          '@context': 'https://schema.org',
          '@type': 'VideoObject',
          name: pageHeading,
          description: description || metaDescription,
          thumbnailUrl: heroImage || youtubeThumbnail,
          uploadDate: videoUploadDate,
          embedUrl: videoEmbedUrl,
        }
      : null;

  return (
    <Page
      scrollingDisabled={scrollingDisabled}
      title={metaTitle}
      description={metaDescription}
      schema={[breadcrumbSchema, collectionSchema, ...(videoSchema ? [videoSchema] : [])]}
      facebookImages={openGraphImages}
      twitterImages={openGraphImages}
      videoEmbedUrl={videoEmbedUrl}
    >
      <TopbarContainer currentPage="CategoryPage" />

      <main className={css.container}>
        <header className={css.header}>
          <CategoryBreadcrumbs
            breadcrumbPath={breadcrumbPath}
            routeConfiguration={routeConfiguration}
          />
          {hasContent ? (
            <CategoryContentSection
              heading={pageHeading}
              description={description}
              heroImage={heroImage}
              videoUrl={videoUrl}
              mediaAttribution={mediaAttribution}
              mediaAttributionName={mediaAttributionName}
              mediaAttributionUrl={mediaAttributionUrl}
            />
          ) : (
            <H1 className={css.title}>{pageHeading}</H1>
          )}
        </header>

        {/* Two-column layout: filter sidebar + listings */}
        <div className={classNames(searchCss.searchMainContent, css.searchMainContent)}>
          {/* Left sidebar — same as SearchPageWithGrid */}
          <aside className={classNames(searchCss.layoutWrapperFilterColumn, css.sidebarColumn)}>
            <ShopByCategoryColumn
              className={css.shopByCategory}
              category={category}
              breadcrumbPath={breadcrumbPath}
              treeCounts={treeCounts}
            />
          </aside>

          {/* Right content */}
          <div className={classNames(searchCss.layoutWrapperMain, css.mainContent)} role="main">
            <div className={searchCss.searchResultContainer}>
              {/* Phone: floating Filter/Sort bar opening the bottom-sheet
                  panel (ledger Phone architecture); hidden from 550px up */}
              <FloatingFilterBar
                onOpenPanel={() => setIsFilterPanelOpen(true)}
                activeCount={activeFilterCount}
                sortConfig={sortConfig}
                urlQueryParams={urlQueryParams}
                onSortBy={handleSortBy}
                isPanelOpen={isFilterPanelOpen}
              />

              {/* Prototype phone: "N listings" line above the rows (the
                  filter row that carries the count is hidden under 550) */}
              {listingsAreLoaded && totalItems != null ? (
                <div className={css.phoneCountLine}>
                  <FormattedMessage id="FilterBar.listingCount" values={{ count: totalItems }} />
                </div>
              ) : null}

              {/* Hybrid filter row (desktop/tablet) + slide-out panel */}
              <div className={css.filterBarWrapper}>
                <FilterBar
                  headFilters={narrowedHeadFilters}
                  overflowFilters={overflowFilters}
                  urlQueryParams={urlQueryParams}
                  initialValues={filterInitialValues}
                  getHandleChangedValueFn={getHandleChangedValueFn}
                  marketplaceCurrency={marketplaceCurrency}
                  listingCategories={listingCategories}
                  intl={intl}
                  totalItems={totalItems}
                  facetsParams={facetsParams}
                  listingsAreLoaded={listingsAreLoaded}
                  sortConfig={sortConfig}
                  onSortBy={handleSortBy}
                  onOpenPanel={() => setIsFilterPanelOpen(true)}
                  onOpenPanelField={fieldKey => {
                    setPanelFocusKey(fieldKey);
                    setIsFilterPanelOpen(true);
                  }}
                  onClearAll={resetAll}
                />
              </div>
              <FilterPanel
                isOpen={isFilterPanelOpen}
                onClose={() => {
                  setIsFilterPanelOpen(false);
                  setPanelFocusKey(null);
                }}
                focusFieldKey={panelFocusKey}
                filters={panelFilters}
                presence={presence}
                urlQueryParams={urlQueryParams}
                initialValues={filterInitialValues}
                getHandleChangedValueFn={getHandleChangedValueFn}
                marketplaceCurrency={marketplaceCurrency}
                listingCategories={listingCategories}
                intl={intl}
                totalItems={totalItems}
                facetsParams={facetsParams}
                onToggleSoldOut={handleToggleSoldOut}
                onClearAll={resetAll}
                onManageDisableScrolling={onManageDisableScrolling}
              />

              {/* Listings */}
              <div
                className={classNames(searchCss.listingsForGridVariant, {
                  [searchCss.newSearchInProgress]: !(listingsAreLoaded || searchListingsError),
                })}
              >
                {searchListingsError && (
                  <Notice tone="error">
                    <p>
                      <FormattedMessage id="CategoryPage.searchError" />
                    </p>
                  </Notice>
                )}

                {listingsAreLoaded && totalItems === 0 && (
                  selectedFiltersCountForMobile === 0 ? (
                    <div className={css.emptyState}>
                      <h2 className={css.emptyHeading}>
                        <FormattedMessage id="CategoryPage.emptyHeading" />
                      </h2>
                      <p className={css.emptyBody}>
                        <FormattedMessage
                          id="CategoryPage.emptyBody"
                          values={{ categoryName: category.name }}
                        />
                      </p>
                      <NamedLink name="NewListingPage" className={css.emptyCta}>
                        <FormattedMessage id="CategoryPage.emptyCta" />
                      </NamedLink>
                    </div>
                  ) : (
                    <div className={css.noResults}>
                      <FormattedMessage id="CategoryPage.noResults" />
                    </div>
                  )
                )}

                {/* No results: no empty results panel (the empty state above says so) */}

                {listings?.length > 0 ? (
  
                  <SearchResultsPanel
                    className={searchCss.searchListingsPanel}
                    listings={listings}
                    pagination={listingsAreLoaded ? pagination : null}
                    search={parse(location.search)}
                    isMapVariant={false}
                    isSearchPage
                    pageName="CategoryPage1Seg"
                    pagePathParams={{ slug: category.url_slug }}
                  />

                ) : null}
              </div>
            </div>
          </div>
        </div>
      </main>

      <BottomNav />
      <FooterContainer />
    </Page>
  );
};

CategoryPageComponent.defaultProps = {
  category: null,
  breadcrumbPath: [],
  listings: [],
  pagination: null,
  searchListingsError: null,
};

CategoryPageComponent.propTypes = {
  category: object,
  breadcrumbPath: array,
  listings: array,
  pagination: propTypes.pagination,
  scrollingDisabled: bool.isRequired,
  searchInProgress: bool.isRequired,
  searchListingsError: propTypes.error,
  config: object.isRequired,
  routeConfiguration: arrayOf(propTypes.route).isRequired,
  onManageDisableScrolling: func.isRequired,
};

const EnhancedCategoryPage = props => {
  const config = useConfiguration();
  const routeConfiguration = useRouteConfiguration();

  return (
    <CategoryPageComponent
      config={config}
      routeConfiguration={routeConfiguration}
      {...props}
    />
  );
};

const mapStateToProps = state => {
  const {
    category,
    breadcrumbPath,
    currentPageResultIds,
    pagination,
    searchInProgress,
    searchListingsError,
  } = state.CategoryPage;

  const listings = getListingsById(state, currentPageResultIds);
  const listingCategories = getListingCategories(state);
  const treeCounts = state.CategoryPage?.treeCounts || null;
  const facets = state.CategoryPage?.facets || null;

  return {
    category,
    breadcrumbPath,
    listings,
    pagination,
    scrollingDisabled: isScrollingDisabled(state),
    searchInProgress,
    searchListingsError,
    listingCategories,
    treeCounts,
    facets,
  };
};

const mapDispatchToProps = dispatch => ({
  onManageDisableScrolling: (componentId, disableScrolling) =>
    dispatch(manageDisableScrolling(componentId, disableScrolling)),
});

const CategoryPage = compose(connect(mapStateToProps, mapDispatchToProps))(EnhancedCategoryPage);

export default CategoryPage;
